// src/components/persons/PersonLocations/PersonLocationMap.jsx
import React, { useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

// IMPORTANTE: Para mapbox-gl v3.x, necesitas configurar el token así
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const DEFAULT_CENTER = { lng: -71.5350, lat: -16.3989 };
const DEFAULT_ZOOM = 13;

export const PersonLocationMap = ({ initialLng, initialLat, onCoordinatesChange }) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const marker = useRef(null);
    const geocoder = useRef(null);

    const updateCoordinates = useCallback(
        (lng, lat) => {
            const roundedLng = Number(lng.toFixed(6));
            const roundedLat = Number(lat.toFixed(6));
            onCoordinatesChange?.(roundedLng, roundedLat);
        },
        [onCoordinatesChange]
    );

    const setMarkerPosition = useCallback(
        (lng, lat) => {
            if (marker.current) {
                marker.current.setLngLat([lng, lat]);
            }
            updateCoordinates(lng, lat);
        },
        [updateCoordinates]
    );

    useEffect(() => {
        // Verificar que el contenedor existe y el mapa no está creado
        if (!mapContainer.current || map.current) return;

        const lng = initialLng ?? DEFAULT_CENTER.lng;
        const lat = initialLat ?? DEFAULT_CENTER.lat;

        // Crear el mapa
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v12', // v12 es más reciente y funciona mejor
            center: [lng, lat],
            zoom: DEFAULT_ZOOM,
        });

        // Esperar a que el mapa cargue
        map.current.on('load', () => {
            if (!map.current) return;

            // Crear marcador
            marker.current = new mapboxgl.Marker({ draggable: true })
                .setLngLat([lng, lat])
                .addTo(map.current);

            // Evento de arrastre del marcador
            marker.current.on('dragend', () => {
                const { lng, lat } = marker.current.getLngLat();
                setMarkerPosition(lng, lat);
            });

            // Evento de click en el mapa
            map.current.on('click', (e) => {
                const { lng, lat } = e.lngLat;
                marker.current.setLngLat([lng, lat]);
                setMarkerPosition(lng, lat);
            });
            
            geocoder.current = new MapboxGeocoder({
                accessToken: mapboxgl.accessToken,
                mapboxgl: mapboxgl,
                placeholder: 'Buscar dirección...',
                marker: false,
                countries: 'pe',
            });

            map.current.addControl(geocoder.current);

            geocoder.current.on('result', (e) => {
                const [lng, lat] = e.result.center;
                marker.current.setLngLat([lng, lat]);
                map.current.flyTo({ center: [lng, lat], zoom: 16 });
                setMarkerPosition(lng, lat);
            });
        });

        // Cleanup
        return () => {
            if (geocoder.current && map.current) {
                try {
                    map.current.removeControl(geocoder.current);
                } catch (e) {
                    console.warn('Error removing geocoder:', e);
                }
                geocoder.current = null;
            }
            if (marker.current) {
                try {
                    marker.current.remove();
                } catch (e) {
                    console.warn('Error removing marker:', e);
                }
                marker.current = null;
            }
            if (map.current) {
                try {
                    map.current.remove();
                } catch (e) {
                    console.warn('Error removing map:', e);
                }
                map.current = null;
            }
        };
    }, []); // Solo se ejecuta al montar

    // Actualizar posición cuando cambian las coordenadas externas
    useEffect(() => {
        if (map.current && map.current.loaded() && marker.current && initialLng && initialLat) {
            const currentPos = marker.current.getLngLat();
            if (currentPos && (currentPos.lng !== initialLng || currentPos.lat !== initialLat)) {
                marker.current.setLngLat([initialLng, initialLat]);
                map.current.flyTo({ center: [initialLng, initialLat], zoom: DEFAULT_ZOOM });
            }
        }
    }, [initialLng, initialLat]);

    return (
        <div
            ref={mapContainer}
            style={{
                width: '100%',
                height: '320px',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#f0f0f0', // Color de fondo mientras carga
            }}
        />
    );
};